// Task #2344 — Reducer puro per l'accumulo degli eventi SSE della chat PA.
// Estratto da PersonalAssistantChat per renderlo testabile in isolamento:
// concentra la semantica di accumulo di contenuto/ragionamento e la gestione
// dei badge tool (nativi provider + tool_call/tool_result), così la logica è
// l'unica fonte di verità e protetta da regressioni.
import { isWebSearchTool } from './paToolDisplay';

/** Accetta solo URL http(s) assoluti: blocca schemi pericolosi (javascript:, data:). */
function isSafeHttpUrl(raw: string): boolean {
  try {
    const u = new URL(raw);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

/** Citazione/fonte web restituita da un tool nativo (es. web search). */
export interface PaCitation {
  url: string;
  title?: string;
  citedText?: string;
}

/**
 * Badge di un tool mostrato in chat. Gli stessi metadati (durata, anteprima
 * risultato, argomenti, fonti) vengono persistiti nel turno e rimappati al
 * reload, così il chip storico è identico a quello live.
 */
export interface PaToolBadge {
  name: string;
  /** Id univoco della singola invocazione (correlazione card↔risultato). */
  id?: string;
  status?: string;
  /** Argomenti del tool. */
  args?: Record<string, unknown>;
  /** Durata esecuzione per-call in ms. */
  durationMs?: number;
  /** Anteprima troncata del risultato. */
  resultPreview?: string;
  /** Fonti citate dal tool nativo (web search). */
  citations?: PaCitation[];
}

/** Meta retrieval Knowledge Base del turno. */
export interface PaRagMeta {
  chunks: number;
  latencyMs: number;
}

/** Stato accumulato per l'intero turno di streaming. */
export interface PaStreamAccumulator {
  /** Testo della risposta accumulato (eventi `token`/`content`). */
  content: string;
  /** Ragionamento dell'AI accumulato (eventi `thinking`). */
  thinking: string;
  /** Tool attivi nel turno (server_tool_use + tool_call/tool_result). */
  activeTools: PaToolBadge[];
  /** Chunk KB recuperati nel turno corrente. */
  rag?: PaRagMeta;
  /** Messaggio di stato operativo emesso dal backend (rate-limit/retry). */
  statusMessage?: string;
}

/** Sottoinsieme tipizzato di un evento SSE rilevante per l'accumulo. */
export interface PaStreamEventInput {
  type?: string;
  content?: string;
  tool?: string;
  /** Id univoco della singola invocazione tool. */
  toolCallId?: string;
  status?: string;
  /** Testo evento `status` (rate-limit/retry) mostrato nel feed unico. */
  message?: string;
  args?: Record<string, unknown>;
  durationMs?: number;
  resultPreview?: string;
  /** Campi evento `citation` (tool nativo web search). */
  url?: string;
  title?: string;
  citedText?: string;
  chunks?: number;
  latencyMs?: number;
}

/** Accumulatore vuoto (inizio turno o reset dopo `done`/`error`). */
export function createPaStreamAccumulator(): PaStreamAccumulator {
  return { content: '', thinking: '', activeTools: [] };
}

/**
 * Applica un evento SSE all'accumulatore restituendo un nuovo stato.
 * Gli eventi non pertinenti all'accumulo lasciano lo stato invariato (identità).
 */
export function reducePaStreamEvent(
  state: PaStreamAccumulator,
  event: PaStreamEventInput,
): PaStreamAccumulator {
  if ((event.type === 'token' || event.type === 'content') && event.content) {
    return { ...state, content: state.content + event.content };
  }

  if (event.type === 'thinking' && event.content) {
    return { ...state, thinking: state.thinking + event.content };
  }

  if (event.type === 'status' && event.message) {
    // Stato operativo del backend (rate-limit/retry): assorbito nel feed unico
    // invece di viaggiare su un binario separato.
    return { ...state, statusMessage: event.message };
  }

  if (event.type === 'server_tool_use' && event.tool) {
    // Tool nativo lato provider (es. web_search Claude): badge unico, già
    // 'success' perché eseguito dal provider senza callback tool_result.
    const tool = event.tool;
    if (state.activeTools.some(t => t.name === tool)) return state;
    return { ...state, activeTools: [...state.activeTools, { name: tool, status: 'success' }] };
  }

  if (event.type === 'tool_call' && event.tool) {
    const tool = event.tool;
    return { ...state, activeTools: [...state.activeTools, { name: tool, id: event.toolCallId, status: 'running', args: event.args }] };
  }

  if (event.type === 'tool_result' && event.toolCallId) {
    const status = event.status ?? '';
    const id = event.toolCallId;
    // Correlazione per id univoco: tool omonimi nello stesso turno non si
    // sovrascrivono a vicenda. Il backend invia sempre `toolCallId` per le
    // invocazioni eseguite (gli unici eventi `tool_result`).
    const activeTools = state.activeTools.map(t =>
      t.id === id
        ? { ...t, status, durationMs: event.durationMs ?? t.durationMs, resultPreview: event.resultPreview ?? t.resultPreview }
        : t,
    );
    return { ...state, activeTools };
  }

  if (event.type === 'citation' && event.url && isSafeHttpUrl(event.url)) {
    // Aggancia la fonte SOLO all'ultimo tool nativo di ricerca web del turno.
    // Senza un badge web-search valido la citazione viene scartata (niente
    // fallback su tool arbitrari → evita mis-attribuzione). Dedup per url.
    const citation: PaCitation = { url: event.url, title: event.title, citedText: event.citedText };
    let targetIdx = -1;
    for (let i = state.activeTools.length - 1; i >= 0; i--) {
      const el = state.activeTools[i];
      if (el && isWebSearchTool(el.name)) { targetIdx = i; break; }
    }
    if (targetIdx === -1) return state;
    const activeTools = state.activeTools.map((t, i) => {
      if (i !== targetIdx) return t;
      const existing = t.citations ?? [];
      if (existing.some(cc => cc.url === citation.url)) return t;
      return { ...t, citations: [...existing, citation] };
    });
    return { ...state, activeTools };
  }

  if (event.type === 'rag') {
    return { ...state, rag: { chunks: event.chunks ?? 0, latencyMs: event.latencyMs ?? 0 } };
  }

  return state;
}
